import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { EsriExpand, EsriReactiveUtils } from '../../../config/esri.module';
import { WidgetNotifyService } from '../../../shared/service/widget-notify.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { MainMapService } from 'src/app/shared/service/main-map.service';
import { ArbeitsbereichService } from '../../../shared/service/arbeitsbereich.service';
import { CONSTANTS } from 'src/app/config/constants.module';
import { WebgisUrlParameter } from './model/webgis-url-parameter.model';
import { WebgisLink } from './model/webgis-link.model';
import { BaseWidget } from '../base-widget/base-widget';

@Component({
    selector: 'share',
    templateUrl: './share.component.html',
    standalone: false
})
export class ShareComponent extends BaseWidget implements OnInit, OnDestroy {
  @ViewChild('shareComponentRef', { static: true }) shareComponentRef: ElementRef;
  @ViewChild('shareMapRef', { static: false }) shareRef: ElementRef;

  private changedMapExtentSubscription: Subscription;

  public inputValue: any;

  constructor(
    public mainMapService: MainMapService,
    public widgetNotifyService: WidgetNotifyService,
    private translate: TranslateService,
    private arbeitsbereichService: ArbeitsbereichService,
    @Inject(CONSTANTS) private CONST: any
  ) {
    super(mainMapService, widgetNotifyService);
  }

  ngOnInit() {
    const { mapView } = this.mainMapService;
    this.handleExpandWidget(mapView);
    const { extent: currentExtent } = this.mainMapService.mapView.extent;
    if (currentExtent) this.inputValue = this.webgisLink(currentExtent);
    this.changedMapExtentSubscription = this.onMapExtentChange();
  }

  /**
   * subscribes to MapExtent changes
   */
  private onMapExtentChange(): Subscription {
    return this.mainMapService.onExtentChangeSubject.subscribe((newExtent: __esri.Extent) => {
      this.inputValue = this.webgisLink(newExtent);
    });
  }

  /**
   * generates a new WebgisLink
   * @param currentExtent the current extent visible on screen
   */
  private webgisLink(currentExtent: __esri.Extent): any {
    const baseUrl = this.getMainUrl();
    const { FUNCTIONS, PARAMETER_NAMES } = this.CONST.APP_URL_PARAMS;
    const currentArbeitsBereichId = this.arbeitsbereichService.selectedArbeitsbereich?.id;

    const functionParam = new WebgisUrlParameter(PARAMETER_NAMES.FUNCTION, FUNCTIONS.ZOOM_TO_WINDOW_AREA);
    const webgisUrlParameterArray: WebgisUrlParameter[] = [];
    webgisUrlParameterArray.push(new WebgisUrlParameter(PARAMETER_NAMES.EASTING1, `${currentExtent.xmin}`));
    webgisUrlParameterArray.push(new WebgisUrlParameter(PARAMETER_NAMES.EASTING2, `${currentExtent.xmax}`));
    webgisUrlParameterArray.push(new WebgisUrlParameter(PARAMETER_NAMES.NORTHING1, `${currentExtent.ymin}`));
    webgisUrlParameterArray.push(new WebgisUrlParameter(PARAMETER_NAMES.NORTHING2, `${currentExtent.ymax}`));
    webgisUrlParameterArray.push(new WebgisUrlParameter(PARAMETER_NAMES.WORKSPACE, currentArbeitsBereichId));

    const webgisLink = new WebgisLink(functionParam, webgisUrlParameterArray);

    return webgisLink.compositedWebgisLink(`${baseUrl}`);
  }

  private handleExpandWidget(mapView: __esri.MapView) {
    this.expandWidget = new EsriExpand(this.expandInstanceProperties(mapView));
    this.mainMapService.registerWidget(this.expandWidget);
    // add widget to the main map
    mapView.ui.add(this.expandWidget, 'top-right');
    // Open this widget, will close other widgets
    EsriReactiveUtils.watch(
      () => this.expandWidget.expanded,
      () => {
        if (this.expandWidget.expanded) {
          this.inputValue = this.webgisLink(this.mainMapService.mapView.extent);
          this.mainMapService.closeOtherWidgets(this.expandWidget);
          this.hiddenContainer = false;
        }
      }
    );
    // expand widget on create
    if (this.expandOnInit) {
      this.expandWidget.expand();
    }
  }

  private expandInstanceProperties(mapView: __esri.MapView): __esri.ExpandProperties {
    return {
      view: mapView,
      id: 'ShareComponent',
      content: this.shareComponentRef.nativeElement,
      expandIcon: 'share',
      collapseTooltip: this.translate.instant('WIDGET.SHARE.TOOLTIP'),
      expandTooltip: this.translate.instant('WIDGET.SHARE.TOOLTIP'),
    };
  }

  /**
   * copies a given link to clipboard
   * @param inputElement the input component to get the text from
   */
  public copyTextToClipboard(inputElement: any) {
    const text = inputElement.value;
    // Copy text using the Clipboard API
    navigator.clipboard
      .writeText(text)
      .then(() => {
        console.log('Text copied to clipboard');
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
      });
    // Reset the selection
    inputElement.setSelectionRange(0, 0);
  }

  /**
   * @function getMainUrl
   * @description get main url from location
   * @return main url
   */
  private getMainUrl() {
    const href = location.href;
    const url = href.split('#');
    return url[0];
  }

  ngOnDestroy() {
    if (!this.changedMapExtentSubscription) return;
    this.changedMapExtentSubscription.unsubscribe();
  }
}
